/**
 * The project's Traces tab (vfs-service, in the person's sandbox pod): who calls whom from the requests through the
 * preview (the service map), the requests of the range, and one request's spans, logs and errors. Same base URL,
 * authorized fetch and ACL headers as the Data tab (DataService.js), and its errors: a 404 without a JSON body means the
 * sandbox runs an older vfs-service (`code: 'sandbox_outdated'`, DataService's SANDBOX_OUTDATED text). Owner, editor and
 * viewer may all read.
 */
import config from '../config';
import { getAuthorizedFetch } from '../utils/fetchRegistry';
import { buildAclContextHeaders } from '../utils/aclContext';
import { DataError, fail } from './DataService';

const base = () => (config.vfsServiceUrl || '').replace(/\/$/, '');
const tracesUrl = (projectId, path) => `${base()}/api/v1/projects/${encodeURIComponent(projectId)}/traces${path}`;

const query = (params) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') { search.set(key, String(value)); }
  });
  const text = search.toString();
  return text ? `?${text}` : '';
};

async function get(url) {
  if (!base()) { throw new DataError('vfs-service is not configured', 0, null); }
  const response = await getAuthorizedFetch()(url, { method: 'GET', headers: { Accept: 'application/json', ...buildAclContextHeaders() } });
  if (!response.ok) { throw await fail(response); }
  return (await response.json().catch(() => null)) || {};
}

/**
 * The service map of the range: `{ enabled, reason?, since, until, totals: { requests, failed, p95Ms, slowMs },
 * perMinute: [{ t, total, failed }], nodes, edges }`. `since`: '5m' | '15m' | '1h' or epoch ms (the server's default: 15m).
 */
export function getMap(projectId, { since, until } = {}) {
  return get(tracesUrl(projectId, `/map${query({ since, until })}`));
}

/**
 * The requests of the range, newest first: `{ traces: [{ traceId, correlationId, entry, entryService, start, durationMs,
 * status, httpStatus?, spanCount, errorCount, services }], total, counts: { all, failed, slow } }`. `q` matches the ids,
 * the entry and any service crossed; `status`: 'all' | 'failed' | 'slow'.
 */
export function listTraces(projectId, { since, q, status, limit = 50 } = {}) {
  return get(tracesUrl(projectId, query({ since, q: q ? q.trim() : undefined, status: status && status !== 'all' ? status : undefined, limit })));
}

/** One request: `{ traceId, correlationId, entry, start, durationMs, status, ..., rows, logs }`; 404 `not_found` when it is no longer kept. */
export function getTrace(projectId, traceId) {
  return get(tracesUrl(projectId, `/${encodeURIComponent(String(traceId))}`));
}
